import { useMemo, useState } from "react"
import { MagnifyingGlass } from "@phosphor-icons/react"

import { Input } from "~/components/design-system/components"
import { ProductCellContent } from "~/components/product/ProductCellContent"
import {
  DialogBodyV2,
  DialogContentV2,
  DialogHeaderV2,
  DialogRoot,
  DialogTitle,
} from "~/components/reusable/Dialog"
import VirtualizedList from "~/components/reusable/VirtualizedList"
import { useVariantMap } from "~/hooks/useVariants"
import { formatNumber, formatProductName } from "~/utils/formatting"
import { formatVariantId } from "~/utils/helpers"

const ROW_HEIGHT = 48
const MAX_LIST_HEIGHT = 400

type ListedVariant = {
  variantId: string
  title: string
  subtitle: string
  imageUrl?: string
}

/**
 * The variants an adjustment applies to, with the variant table's identity treatment: image, name
 * and id per row. Read-only — mount it controlled, without a trigger.
 */
export function VariantListDialog({
  variantIds,
  open,
  onClose,
}: {
  variantIds: string[]
  open: boolean
  onClose: () => void
}) {
  return (
    <DialogRoot
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose()
      }}
    >
      <DialogContentV2 className="max-w-[600px]">
        <DialogHeaderV2>
          <DialogTitle>
            {formatNumber(variantIds.length, 0)} variant{variantIds.length === 1 ? "" : "s"}
          </DialogTitle>
        </DialogHeaderV2>
        <DialogBodyV2>
          <VariantList variantIds={variantIds} />
        </DialogBodyV2>
      </DialogContentV2>
    </DialogRoot>
  )
}

/** Split out so the names resolve — and the search resets — with the dialog, not per table row. */
function VariantList({ variantIds }: { variantIds: string[] }) {
  const variantMap = useVariantMap()
  const [searchText, setSearchText] = useState("")

  const variants = useMemo<ListedVariant[]>(
    () =>
      variantIds
        .map((variantId) => {
          const variant = variantMap.get(variantId)
          const id = formatVariantId(variantId)
          return {
            variantId,
            // Falls back to the id for a variant the SKU store doesn't hold.
            title: variant ? formatProductName(variant.variantName, variant.productName) : id,
            subtitle: `#${id}`,
            imageUrl: variant?.imageUrl ?? undefined,
          }
        })
        .sort((a, b) => a.title.localeCompare(b.title)),
    [variantIds, variantMap],
  )

  const filtered = useMemo(() => {
    const search = searchText.toLowerCase().trim()
    if (!search) return variants
    return variants.filter(
      (variant) =>
        variant.title.toLowerCase().includes(search) ||
        variant.subtitle.toLowerCase().includes(search),
    )
  }, [variants, searchText])

  return (
    <>
      <Input
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        placeholder="Search variants..."
        small
        clearButton
        containerClassName="w-full"
        className="pl-7"
        icon={
          <MagnifyingGlass
            size={14}
            className="absolute left-2 top-1/2 -translate-y-1/2 text-fg-muted pointer-events-none"
          />
        }
      />
      {filtered.length === 0 ? (
        <div className="py-8 text-center text-sm text-fg-disabled">
          No variants match your search
        </div>
      ) : (
        <VirtualizedList
          // Fixed height, so cap it to the rows actually present or a short list reserves blank space.
          height={Math.min(MAX_LIST_HEIGHT, filtered.length * ROW_HEIGHT)}
          estimateSize={ROW_HEIGHT}
          items={filtered}
          keyExtractor={(variant) => variant.variantId}
          renderItem={(variant) => (
            <ProductCellContent
              imageUrl={variant.imageUrl}
              title={variant.title}
              subtitle={variant.subtitle}
            />
          )}
        />
      )}
    </>
  )
}
